<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="results"
      item-key="user.uid"
      :items-per-page="40"
      :footer-props="footerProps"
      :custom-filter="filter"
      sort-by="user.lastName"
      :search="search"
      :group-by.sync="groupBySelector.value"
      class="rounded"
      max-height="800px auto"
      :mobile-breakpoint="0"
      dense
    >
      <template v-slot:group.header="{ items, isOpen, toggle }">
        <th :colspan="headers.length">
          <div class="my-1">
            <v-icon @click="toggle" class="mr-1"
              >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
            <span>
              {{ items[0].groupByDisplayText[groupBySelector.value] }}
            </span>
          </div>
        </th>
      </template>
      <template v-slot:item="{ item }">
        <tr class="parent-tr">
          <td class="inner-td">
            {{ item.user.firstName }}
          </td>
          <td
            class="inner-td"
            style="border-right: thin solid rgba(0, 0, 0, 0.12) !important"
          >
            {{ item.user.lastName }}
          </td>
          <td style="border-right: thin solid rgba(0, 0, 0, 0.12) !important">
            <performance-target-status-chip
              :status="item.totalPerformanceTargetResultStatus"
              small
            ></performance-target-status-chip>
          </td>
          <td style="border-right: thin solid rgba(0, 0, 0, 0.12) !important">
            <performance-target-traffic-light
              :status="item.trafficLightStatus"
              small
            ></performance-target-traffic-light>
          </td>
          <td
            v-for="target in performanceTargets"
            :key="target.meta.id"
            :class="
              getCellClassByStatus(
                getResultForTarget(item, target.meta.id)?.status
              )
            "
            style="border-right: thin solid rgba(0, 0, 0, 0.12) !important"
          >
            <template v-if="getResultForTarget(item, target.meta.id)">
              <template v-if="getPrimaryDataVariable(item, target)">
                <v-chip
                  small
                  label
                  outlined
                  class="mr-1 my-1 font-weight-medium"
                  light
                >
                  {{ Math.ceil(getPrimaryDataVariable(item, target).value) }}
                  {{ getPrimaryDataVariable(item, target).unit || "" }}
                </v-chip>
              </template>
              <span v-else class="grey--text text--lighten-1 caption"> – </span>
            </template>
            <span v-else class="grey--text text--lighten-1 caption"> – </span>
          </td>
          <td>
            <v-btn text outlined small @click="toggleDetailsDialog(item)"
              ><v-icon small>mdi-calculator-variant</v-icon></v-btn
            >
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Performance Target Results Details Dialog -->
    <CustomDialog
      v-if="helpers.detailsDialog.model"
      @close="toggleDetailsDialog()"
      title="Berechnungsdetails"
      fullscreenOnMobile
      width="l"
      hideActionBtn
      showCloseBtn
    >
      <template v-slot:content>
        <performance-target-results-details-content
          v-if="helpers.detailsDialog.data"
          :user-data="helpers.detailsDialog.data"
        ></performance-target-results-details-content>
      </template>
    </CustomDialog>
  </div>
</template>

<script>
import PerformanceTargetStatusChip from "@/components/member-management/performance-targets/PerformanceTargetStatusChip.vue";
import PerformanceTargetTrafficLight from "@/components/member-management/performance-targets/PerformanceTargetTrafficLight.vue";
import PerformanceTargetResultsDetailsContent from "@/components/member-management/performance-targets/PerformanceTargetResultsDetailsDialog.vue";
import CustomDialog from "@/components/_systemwide/CustomDialog.vue";
export default {
  name: "performance-targets-results-data-table",
  props: {
    results: {
      type: Array,
      required: true,
    },
    search: {
      type: String,
      required: false,
    },
    groupBySelector: {
      type: Object,
      required: true,
    },
  },
  components: {
    PerformanceTargetStatusChip,
    PerformanceTargetTrafficLight,
    PerformanceTargetResultsDetailsContent,
    CustomDialog,
  },
  data() {
    return {
      helpers: {
        dataIsLoading: false,
        detailsDialog: {
          model: false,
          data: null,
        },
      },
      footerProps: {
        "items-per-page-options": [25, 40, 50, 100, -1],
      },
    };
  },
  computed: {
    performanceTargets() {
      return this.$store.state.memberManagement.performanceTargets;
    },
    headers() {
      const performanceTargetHeaders = this.performanceTargets.map(
        (target) => ({
          text: target.shortTitle,
          value: target.id,
          divider: true,
        })
      );
      return [
        {
          text: "Vorname",
          value: "user.firstName",
          cellClass: "v-row-group__header",
        },
        {
          text: "Nachname",
          value: "user.lastName",
          cellClass: "v-row-group__header",
          divider: true,
        },
        {
          text: "Status",
          value: "totalPerformanceTargetResultStatus",
          divider: true,
        },
        { text: "Ampel", value: "trafficLightStatus", divider: true },
        ...performanceTargetHeaders,
        {
          text: "Berechnung",
          width: "1%",
          align: "center",
        },
      ];
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {},
    filter(value, search, item) {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return Object.values(item).some((prop) => {
        if (typeof prop === "object" && prop !== null) {
          return Object.values(prop).some((subProp) =>
            String(subProp).toLowerCase().includes(searchLower)
          );
        }
        return String(prop).toLowerCase().includes(searchLower);
      });
    },
    async toggleDetailsDialog(item) {
      if (item) {
        this.helpers.detailsDialog.data = item;
      }
      this.helpers.detailsDialog.model = !this.helpers.detailsDialog.model;
    },

    stripPrefix(item) {
      return item.substring(item.indexOf("_") + 1);
    },
    getResultForTarget(item, targetId) {
      if (
        !item.performanceTargetResults ||
        typeof item.performanceTargetResults !== "object"
      ) {
        return null;
      }
      // Handle object structure where keys are target IDs
      return item.performanceTargetResults[targetId] || null;
    },
    getPrimaryDataVariable(item, target) {
      const result = this.getResultForTarget(item, target.meta.id);
      if (
        !result ||
        !result.dataVariables ||
        result.dataVariables.length === 0
      ) {
        return null;
      }
      // Find the primary data variable
      const primaryVariable = result.dataVariables.find(
        (v) => v.variable.id === target.primaryDataVariable
      );
      return primaryVariable || null;
    },
    getCellClassByStatus(status) {
      return {
        "green lighten-4 green--text text--darken-4": status === "met",
        "orange lighten-4 orange--text text--darken-4": status === "unknown",
        "red lighten-4 red--text text--darken-4": status === "notMet",
      };
    },
  },
};
</script>
